import { useCallback, useState } from "react";
import { enqueueSnackbar } from "notistack";

type ItemAction = "accept" | "decline";

interface UseItemActionOptions {
  approveItem: (id: number) => Promise<unknown>;
  declineItem: (id: number) => Promise<unknown>;
}

interface UseItemActionReturn {
  handleAccept: (id: number) => Promise<boolean>;
  handleDecline: (id: number) => Promise<boolean>;
  getItemLoadingAction: (id: number) => ItemAction | undefined;
}

export function useItemAction({
  approveItem,
  declineItem,
}: UseItemActionOptions): UseItemActionReturn {
  const [loadingActions, setLoadingActions] = useState<Map<number, ItemAction>>(() => new Map());

  const runAction = useCallback(
    async (
      id: number,
      action: ItemAction,
      mutate: (id: number) => Promise<unknown>,
      successMsg: string,
      errorMsg: string,
    ): Promise<boolean> => {
      setLoadingActions((prev) => new Map(prev).set(id, action));
      try {
        await mutate(id);
        enqueueSnackbar(successMsg, { variant: "success" });
        return true;
      } catch {
        enqueueSnackbar(errorMsg, { variant: "error" });
        return false;
      } finally {
        setLoadingActions((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [],
  );

  const handleAccept = useCallback(
    (id: number) =>
      runAction(id, "accept", approveItem, "Added to inventory", "Failed to accept item"),
    [runAction, approveItem],
  );

  const handleDecline = useCallback(
    (id: number) => runAction(id, "decline", declineItem, "Item removed", "Failed to decline item"),
    [runAction, declineItem],
  );

  const getItemLoadingAction = useCallback(
    (id: number): ItemAction | undefined => loadingActions.get(id),
    [loadingActions],
  );

  return { handleAccept, handleDecline, getItemLoadingAction };
}
